// User profile service for Firestore operations
import { doc, setDoc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { generateRandomUsername } from '../utils/usernameUtils';

export const createUserProfile = async (user) => {
  try {
    const userRef = doc(db, 'users', user.uid);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      // Generate a unique username
      let username = generateRandomUsername();
      let isUnique = false;
      let attempts = 0;
      
      while (!isUnique && attempts < 10) {
        const usernameQuery = query(
          collection(db, 'users'),
          where('username', '==', username)
        );
        const querySnapshot = await getDocs(usernameQuery);
        
        if (querySnapshot.empty) {
          isUnique = true;
        } else {
          username = generateRandomUsername();
          attempts++;
        }
      }
      
      // Create user profile
      const userProfile = {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || username,
        username: username,
        photoURL: user.photoURL,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isFirstLogin: true
      };
      
      await setDoc(userRef, userProfile);
      return userProfile;
    } else {
      // Update existing user profile
      const existingProfile = userSnap.data();
      const updatedProfile = {
        ...existingProfile,
        email: user.email,
        displayName: user.displayName || existingProfile.displayName,
        photoURL: user.photoURL,
        updatedAt: new Date().toISOString()
      };
      
      await updateDoc(userRef, updatedProfile);
      return updatedProfile;
    }
  } catch (error) {
    console.error('Error creating/updating user profile:', error);
    throw error;
  }
};

export const getUserProfile = async (uid) => {
  try {
    const userRef = doc(db, 'users', uid);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      return userSnap.data();
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error getting user profile:', error);
    throw error;
  }
};

export const getUserProfileByUsername = async (username) => {
  try {
    const usersQuery = query(
      collection(db, 'users'),
      where('username', '==', username)
    );
    const querySnapshot = await getDocs(usersQuery);
    
    if (!querySnapshot.empty) {
      return querySnapshot.docs[0].data();
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error getting user profile by username:', error);
    throw error;
  }
};

export const updateUserProfile = async (uid, updates) => {
  try {
    const userRef = doc(db, 'users', uid);
    const updateData = {
      ...updates,
      updatedAt: new Date().toISOString()
    };
    
    await updateDoc(userRef, updateData);
    return updateData;
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

export const checkUsernameAvailability = async (username, currentUid = null) => {
  try {
    const usernameQuery = query(
      collection(db, 'users'),
      where('username', '==', username)
    );
    const querySnapshot = await getDocs(usernameQuery);
    
    if (querySnapshot.empty) {
      return { available: true };
    }
    
    // Check if the username belongs to the current user
    const existingUser = querySnapshot.docs[0].data();
    if (currentUid && existingUser.uid === currentUid) {
      return { available: true };
    }
    
    return { available: false };
  } catch (error) {
    console.error('Error checking username availability:', error);
    throw error;
  }
};

export const updateUsername = async (uid, newUsername) => {
  try {
    // Check if username is available
    const availability = await checkUsernameAvailability(newUsername, uid);
    
    if (!availability.available) {
      throw new Error('Username is already taken');
    }
    
    // Update username
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, {
      username: newUsername,
      updatedAt: new Date().toISOString()
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error updating username:', error);
    throw error;
  }
};
